import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, AngebotVersion, Belegvorschau as PreviewState } from "@kundeninfo/api-client";
import { Modal } from "@kundeninfo/ui";
import { Hinweis } from "@kundeninfo/ui/me-design";
import { PreviewPages } from "../Belegvorschau.js";
import { apiMeldung } from "../vorgangsdetail.js";
import { formatDatum } from "../../format.js";
import { SUCCESSION_TEXT as T } from "./texts.js";

/** Die Auflösung im seitlichen Panel (`breit`, 900 px): ein A4-Blatt bei 96 dpi ist rund 790 px breit und füllt die
 *  Spalte, ohne hochgezogen zu werden — die Vorgabe der Vorschau (110 dpi) wäre hier nur Last. */
const VIEW_DPI = 96;

/**
 * „Ansehen“ einer älteren Angebotsversion (Dirk 484, Spec „Kopieren und Nachfolge“): die Seitenbilder GENAU DIESER
 * Version, lesend, im seitlichen Panel (`Modal` = `unter="seitlich"`, Kreuz und Escape schliessen). Keine Knöpfe zum
 * Bearbeiten, kein Neu-Rendern — wer an der Version weiterarbeiten will, nimmt „Als Vorlage für neue Version“.
 *
 * Eine Version, die nie hinausging, hat kein festgeschriebenes Blatt: Der Server rendert sie mit den Textbausteinen
 * und dem Layout von HEUTE (`neverSent`). Das sagt der Hinweis über dem ersten Blatt, damit niemand das Bild für
 * den damaligen Stand hält.
 */
export function VersionView(p: {
  client: Pick<ApiClient, "vorschauOffer">;
  kundeId: string;
  version: AngebotVersion;
  onSchliessen: () => void;
}): ReactElement {
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [error, setError] = useState<string | null>(null);
  /* Einmal je VERSION gerendert, kein Cache (wie die Belegvorschau). Der Riegel merkt sich die Kennung, für die schon
     abgerufen wurde: Er hält React 19 im Entwicklungsmodus von einem zweiten Abruf ab, lässt aber eine andere Version
     neu abrufen (Review 28 K3) — und eine späte Antwort der vorigen überschreibt die jetzige nicht. */
  const requested = useRef<string | null>(null);
  useEffect(() => {
    const id = p.version.id;
    if (requested.current === id) return;
    requested.current = id;
    setPreview(null);
    setError(null);
    p.client.vorschauOffer(p.kundeId, id, VIEW_DPI).then(
      (state) => { if (requested.current === id) setPreview(state); },
      (e: unknown) => { if (requested.current === id) setError(apiMeldung(e, T.viewFailed)); });
  }, [p.client, p.kundeId, p.version.id]);

  return (
    <Modal breite="breit" titel={T.viewTitle(p.version.versionNumber, formatDatum(p.version.createdOn))}
      onAbbrechen={p.onSchliessen}>
      {error !== null && <Hinweis art="fehler">{error}</Hinweis>}
      {preview === null && error === null && <p className="offer-version-view__loading">{T.viewLoading}</p>}
      {preview?.neverSent === true && <Hinweis art="info">{T.neverSent}</Hinweis>}
      {preview !== null && (
        <div className="offer-version-view__pages bvor__seiten--breite">
          <PreviewPages seiten={preview.seiten} />
        </div>
      )}
    </Modal>
  );
}
